import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { Download, FileText, Shield, Check, Mail, MapPin, Clock } from 'lucide-react';

// DPA — Swiss Minimalist + Pastel theme.
// Pastels: sage #D4E0DA · peach #EBD8C8 · lavender #D9CFD9
// Fonts: .font-display · .font-body · .font-label

const DPA = () => {
  const navigate = useNavigate();

  const highlights = [
    { tint: '#D4E0DA', title: 'GDPR & CCPA compliant',          description: 'Full compliance with EU GDPR, UK GDPR, Swiss DPA, and California CCPA/CPRA.' },
    { tint: '#EBD8C8', title: 'Standard contractual clauses',   description: 'EU SCCs, UK Transfer Addendum, and Swiss modifications for international transfers.' },
    { tint: '#D9CFD9', title: 'Enterprise-grade security',      description: 'AES-256 encryption, MFA, WAF, 24/7 monitoring, and SOC 2 Type II commitment.' },
    { tint: '#D4E0DA', title: '24-hour breach notification',    description: 'Rapid incident response with notification within 24 hours of discovery.' },
    { tint: '#EBD8C8', title: 'Transparent sub-processors',     description: 'Full disclosure of sub-processors with 30-day advance notice of changes.' },
    { tint: '#D9CFD9', title: 'Data subject rights support',    description: 'Assistance with access, rectification, erasure, and portability requests.' },
  ];

  const sections = [
    'Definitions and Scope',
    'Roles of the Parties',
    'Data Processing Terms',
    'Security Measures',
    'Third-Party Integrations',
    'International Transfers',
    'Security Incidents',
    'Data Retention & Deletion',
    'Warranties & Compliance',
    'Liability & Indemnification',
  ];

  const annexes = [
    'Annex 1 — Processing Activities Description',
    'Annex 2 — Technical & Organizational Security Measures',
    'Annex 3 — Sub-Processors List',
    'Annex 4 — Security Incident Response Procedures',
    'Annex 5 — Data Subject Rights Procedures',
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] font-body">
      <Header />

      {/* ===================== HERO ===================== */}
      <section
        className="bg-[#EBD8C8]/55 dark:bg-[#2E1B10]/55 pt-24 md:pt-32 pb-24 md:pb-32 px-6 border-b border-black/10 dark:border-white/10 min-h-[480px] md:min-h-[640px]"
        data-testid="dpa-hero"
      >
        <div className="max-w-5xl mx-auto text-center w-full">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-black/55 dark:text-white/55 mb-8">
            Data Protection
          </p>
          <h1 className="font-display font-semibold leading-[1.25] tracking-tight text-2xl sm:text-3xl lg:text-4xl mb-8">
            Data Processing
            <br />
            <span className="italic text-black/85 dark:text-white/90">Addendum.</span>
          </h1>
          <p className="font-body text-base md:text-lg text-black/70 dark:text-white/70 max-w-3xl mx-auto leading-relaxed mb-10">
            Our commitment to protecting your data and ensuring compliance with global privacy regulations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-dpa.pdf', '_blank')}
              data-testid="dpa-download-hero"
              className="font-label bg-[#0A0A0A] dark:bg-[#141414] text-[#FAFAFA] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:bg-[#D4E0DA] dark:hover:bg-[#101F18] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Full DPA
            </button>
            <button
              type="button"
              onClick={() => navigate('/contact')}
              data-testid="dpa-contact"
              className="font-label border border-[#0A0A0A]/20 dark:border-white/25 text-[#0A0A0A] dark:text-[#F5F5F5] px-8 py-4 rounded-full text-xs uppercase tracking-[0.2em] hover:border-[#0A0A0A] dark:hover:border-white/30 hover:bg-[#D9CFD9]/40 dark:hover:bg-[#1E1724]/40 transition-all duration-500"
            >
              Contact Privacy Team
            </button>
          </div>
        </div>
      </section>

      {/* ===================== DOCUMENT INFO + OVERVIEW ===================== */}
      <section className="px-6 py-24 md:py-28" data-testid="dpa-overview">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-16 items-start">
          <div>
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-4">
              Overview
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-8 leading-tight">
              A clear contract for your data.
            </h2>
            <div className="space-y-5 text-base md:text-lg text-black/75 dark:text-white/75 leading-relaxed">
              <p>
                This Data Processing Addendum ("DPA") is entered into between <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Inceptica LLC</strong>, doing business as <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">Nova</strong>, and the Customer. It forms part of any agreement, terms of service, or order under which Customer uses Nova's compliance automation and assessment platform and related services.
              </p>
              <p>
                By using the Services, Customer agrees to this DPA. The document outlines our data processing practices, security measures, and commitments to protecting your Personal Data in compliance with <strong className="text-[#0A0A0A] dark:text-[#F5F5F5]">GDPR, CCPA</strong>, and other applicable Data Protection Laws.
              </p>
            </div>
          </div>

          <div className="rounded-2xl bg-[#D4E0DA]/45 dark:bg-[#101F18]/45 p-8 md:p-10">
            <div className="flex items-start gap-3 mb-5">
              <FileText className="w-5 h-5 mt-0.5 text-black/65 dark:text-white/65" strokeWidth={1.5} />
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55">
                Document Information
              </p>
            </div>
            <div className="space-y-5">
              <div>
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Effective Date</p>
                <p className="font-display text-lg md:text-xl leading-tight">Date Customer first uses the Services</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Last Updated</p>
                <p className="font-display text-lg md:text-xl leading-tight">November 2025</p>
              </div>
              <div className="border-t border-black/10 dark:border-white/10 pt-5">
                <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Format</p>
                <p className="font-display text-lg md:text-xl leading-tight">50+ pages, 20 sections, 5 annexes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== KEY HIGHLIGHTS (BENTO) ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="dpa-highlights">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              Key Highlights
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4">
              What's <span className="italic text-black/85 dark:text-white/90">guaranteed.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-black/10 dark:border-white/10">
            {highlights.map((h, i) => (
              <div
                key={h.title}
                data-testid={`dpa-highlight-${i}`}
                className="group relative border-r border-b border-black/10 dark:border-white/10 p-8 md:p-10 transition-colors duration-700 flex flex-col"
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = h.tint + '40'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-8 nv-tint"
                  style={{ backgroundColor: h.tint + 'CC' }}
                >
                  <Shield className="w-5 h-5 text-[#0A0A0A] dark:text-[#F5F5F5]" strokeWidth={1.5} />
                </div>
                <p
                  className="font-display font-semibold leading-none text-5xl md:text-6xl mb-5 select-none"
                  style={{ color: h.tint }}
                >
                  {String(i + 1).padStart(2, '0')}
                </p>
                <h3 className="font-display text-lg md:text-xl leading-tight mb-3">{h.title}</h3>
                <p className="font-body text-sm md:text-base text-black/65 dark:text-white/65 leading-relaxed">{h.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== WHAT'S INCLUDED ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="dpa-included">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
              What's Included
            </p>
            <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight mb-4 leading-tight">
              The complete document.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Main Sections (1–20)
              </p>
              <ul className="space-y-3">
                {sections.map((s) => (
                  <li key={s} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-sm md:text-base text-black/75 dark:text-white/75 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-black/10 dark:border-white/10 p-8 md:p-10 bg-[#FAFAFA] dark:bg-[#050505]">
              <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-5">
                Annexes (1–5)
              </p>
              <ul className="space-y-3">
                {annexes.map((a) => (
                  <li key={a} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 shrink-0 text-black/55 dark:text-white/55" strokeWidth={1.5} />
                    <span className="font-body text-sm md:text-base text-black/75 dark:text-white/75 leading-relaxed">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== CONTACT ===================== */}
      <section className="px-6 pb-24 md:pb-32" data-testid="dpa-contact-section">
        <div className="max-w-5xl mx-auto rounded-2xl bg-[#D9CFD9]/45 dark:bg-[#1E1724]/45 p-8 md:p-12">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
            <div>
              <p className="font-label text-[11px] uppercase tracking-[0.25em] text-black/55 dark:text-white/55 mb-3">
                Questions
              </p>
              <h3 className="font-display font-semibold text-2xl md:text-4xl tracking-tight leading-tight">
                Speak with our Data Protection Officer.
              </h3>
            </div>
            <ul className="space-y-5">
              <li className="flex items-start gap-4">
                <Mail className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Email</p>
                  <a href="mailto:privacy@teamnova.ai" className="font-display text-lg md:text-xl leading-tight underline-offset-4 hover:underline">privacy@teamnova.ai</a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <MapPin className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Address</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">2389 Main St. STE 100, Glastonbury, CT 06033</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <Clock className="w-4 h-4 mt-1.5 shrink-0 text-black/65 dark:text-white/65" strokeWidth={1.5} />
                <div>
                  <p className="font-label text-[10px] uppercase tracking-[0.22em] text-black/55 dark:text-white/55 mb-1">Response Time</p>
                  <p className="font-body text-base text-black/80 dark:text-white/85 leading-relaxed">Within 48 hours · 24 hours for urgent matters</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ===================== FINAL CTA (INVERTED) ===================== */}
      <section className="bg-[#0A0A0A] dark:bg-[#141414] py-24 md:py-32 px-6 text-center" data-testid="dpa-cta-section">
        <div className="max-w-4xl mx-auto">
          <p className="font-label text-[11px] uppercase tracking-[0.3em] text-white/65 mb-6">
            Download
          </p>
          <h2 className="font-display font-semibold text-3xl md:text-4xl text-[#FAFAFA] mb-6 leading-[1.1] tracking-tight">
            Read the full document.
          </h2>
          <p className="font-body text-sm md:text-base text-white/70 mb-12 max-w-2xl mx-auto">
            Access all 20 sections and 5 detailed annexes — including security measures, sub-processors, and data subject rights procedures.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => window.open('/ethira-dpa.pdf', '_blank')}
              data-testid="dpa-cta-download"
              className="font-label bg-[#FAFAFA] dark:bg-[#050505] text-[#0A0A0A] dark:text-[#F5F5F5] hover:bg-[#EBD8C8] dark:hover:bg-[#2E1B10] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500 inline-flex items-center gap-2"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5} />
              Download Full DPA (PDF)
            </button>
            <button
              type="button"
              onClick={() => navigate('/security')}
              data-testid="dpa-cta-security"
              className="font-label border border-white/25 bg-transparent text-[#FAFAFA] hover:bg-[#FAFAFA] dark:hover:bg-[#050505] hover:text-[#0A0A0A] dark:hover:text-[#F5F5F5] uppercase tracking-[0.2em] text-xs px-8 py-4 rounded-full transition-all duration-500"
            >
              Our Security Practices
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DPA;
